import { z } from 'zod';
import { CustomerPricing } from './customer-pricing.js';
const amount = z.number().finite().nonnegative().max(1e9);
const quantity = z.number().finite().nonnegative().max(1e8);
const cents = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
export const SettlementAdjustment = z.object({
    line_index: z.number().int().nonnegative(),
    quantity,
    measured_quantity: quantity.optional(),
    wastage_quantity: quantity.optional(),
    note: z.string().trim().max(1000).default(''),
}).refine(a => (a.measured_quantity === undefined) === (a.wastage_quantity === undefined), { message: 'Enter both measured and wastage quantities.' })
    .refine(a => a.measured_quantity === undefined || Math.abs(a.measured_quantity + a.wastage_quantity - a.quantity) < 0.000001, { message: 'Final quantity must equal measured quantity plus wastage.' });
export const SettlementInput = z.object({
    adjustments: z.array(SettlementAdjustment).max(200).default([]),
    // The operator reviews the adjusted calculation BEFORE choosing a new amount.
    rounding: z.object({ calculated_subtotal: amount, final_subtotal: amount }).nullable().default(null),
});
/** This is settlement arithmetic over frozen accepted rates, not a pricing
 * engine. Only the explicit commercial rounding line is discarded. Discounts
 * negotiated into unit rates and other accepted discount lines remain intact. */
export function calculateJobSettlement(accepted, input, gstRate) {
    const pricing = CustomerPricing.parse(accepted), edit = SettlementInput.parse(input);
    if (!Number.isFinite(gstRate) || gstRate < 0 || gstRate > 1)
        throw Error('Invalid accepted GST rate');
    const changed = new Map();
    for (const adjustment of edit.adjustments) {
        if (changed.has(adjustment.line_index))
            throw Error('Duplicate settlement line');
        const original = pricing.lines[adjustment.line_index];
        if (!original || original.selection_id === 'commercial-rounding')
            throw Error('Settlement line is unavailable');
        changed.set(adjustment.line_index, adjustment);
    }
    const lines = pricing.lines.flatMap((line, line_index) => {
        if (line.selection_id === 'commercial-rounding')
            return [];
        const adjustment = changed.get(line_index), finalQuantity = adjustment?.quantity ?? line.quantity;
        let sell = line.included_in_price ? 0 : line.sell;
        if (sell === null)
            throw Error('An exact accepted amount is required for settlement');
        // Preserve the frozen amount when quantity is unchanged: a displayed unit
        // rate may have fewer decimals than the engine's original calculation.
        if (finalQuantity !== line.quantity && !line.included_in_price) {
            if (line.unitSell === null)
                throw Error('An accepted unit rate is required to adjust quantity');
            sell = cents(finalQuantity * line.unitSell);
        }
        return [{ ...line, line_index, accepted_quantity: line.quantity, accepted_sell: line.included_in_price ? 0 : line.sell,
                quantity: finalQuantity, sell, measured_quantity: adjustment?.measured_quantity ?? null,
                wastage_quantity: adjustment?.wastage_quantity ?? null, adjustment_note: adjustment?.note ?? '' }];
    });
    const calculated_subtotal = cents(lines.reduce((n, line) => n + line.sell, 0));
    if (calculated_subtotal < 0 || calculated_subtotal > 1e9)
        throw Error('Invalid settlement subtotal');
    if (edit.rounding && cents(edit.rounding.calculated_subtotal) !== calculated_subtotal)
        throw Error('Quantities changed. Review the calculation and enter rounding again.');
    const entered = edit.rounding ? cents(edit.rounding.final_subtotal) : calculated_subtotal;
    if (entered > calculated_subtotal)
        throw Error('The rounded amount cannot exceed the calculated subtotal');
    // The settled total ex GST ends on the whole dollar, rounded down, like the accepted one (16 Oct 2026).
    const subtotal = Math.floor(entered + 1e-9);
    const gst = cents(subtotal * gstRate);
    return { lines, calculated_subtotal, rounding_discount: cents(calculated_subtotal - subtotal), subtotal, gst, total: cents(subtotal + gst), gst_rate: gstRate };
}
/** Call whenever a final quantity, measurement or wastage changes. Never carry
 * an earlier settlement rounding choice into a changed calculation. */
export function changeSettlementQuantity(input, adjustment) {
    const next = SettlementAdjustment.parse(adjustment);
    return { adjustments: [...input.adjustments.filter(a => a.line_index !== next.line_index), next], rounding: null };
}
export function settlementBalance(total, deposit, receipts) {
    amount.parse(total);
    if (deposit !== null)
        amount.parse(deposit);
    receipts.forEach(n => amount.parse(n));
    const paid = cents((deposit ?? 0) + receipts.reduce((sum, n) => sum + n, 0));
    return { deposit: deposit ?? 0, deposit_recorded: deposit !== null, paid, balance: cents(total - paid) };
}
//# sourceMappingURL=job-settlement.js.map